<template>
  <CModal :title="$t('sendReport.modalTitle')" size="lg" :show.sync="localModal" :close-on-backdrop="false">
    <CModalBody class="p-4 body-font">
      <CForm>
        <div class="d-flex align-items-center mb-4">
          <div class="icon-box bg-gradient-warning mr-3">
            <span class="text-white"><CIcon name="cil-settings" size="xl" /></span>
          </div>
          <div>
            <h5 class="m-0 font-weight-bold">{{ $t('sendReport.sectionRepair') }}</h5>
            <small class="text-muted">{{ $t('sendReport.sectionRepairHint') }}</small>
          </div>
        </div>

        <div class="mb-3">
          <label class="lbl">{{ $t('sendReport.jobType') }} <span class="text-danger">*</span></label>

          <!-- Predefined categories multiselect (Single Select) -->
          <div>
            
            <div class="job-type-grid">
              <div 
                v-for="option in allSubCategoryOptions" 
                :key="option.value" 
                class="job-item-wrapper"
                @click="formData.selectedSubCategory = option"
                :title="option.label"
              >
                <div 
                  class="job-card" 
                  :class="{ active: formData.selectedSubCategory && formData.selectedSubCategory.value === option.value }"
                >
                  <div class="check" v-if="formData.selectedSubCategory && formData.selectedSubCategory.value === option.value">✓</div>
                  <div :class="getJobIconColor(option.label)">
                    <img v-if="option.icon" :src="`${appUrl}${option.icon}`" width="32" height="32" alt="icon" style="object-fit: contain;" />
                    <img v-else-if="getCustomIconUrl(option.label)" :src="getCustomIconUrl(option.label)" width="32" height="32" alt="icon" style="object-fit: contain;" />
                    <CIcon v-else :name="getJobIcon(option.label)" size="lg"/>
                  </div>
                </div>
              </div>
              
              <div v-if="allSubCategoryOptions.length === 0" class="text-center text-muted w-100 p-3 bg-light rounded">
                <small>{{ $t('sendReport.noJobTypes') }}</small>
              </div>
            </div>

            <div v-if="errors.subCategories" class="text-danger mt-2">
              {{ errors.subCategories }}
            </div>

            <!-- Other category input -->
            <transition name="fade">
              <div v-if="formData.selectedSubCategory && formData.selectedSubCategory.value === 'other'" class="mt-3">
                <label class="lbl">{{ $t('sendReport.otherTypeLabel') }} <span class="text-danger">*</span></label>
                <CInput 
                  v-model="formData.otherCategoryText" 
                  :placeholder="$t('sendReport.otherTypePlaceholder')"
                  :class="{ 'is-invalid': errors.otherCategoryText }" 
                />
                <div v-if="errors.otherCategoryText" class="invalid-feedback d-block">
                  {{ errors.otherCategoryText }}
                </div>
              </div>
            </transition>
          </div>
        </div>

        <div v-if="requiresMachineNumberImage" class="my-3">
          <label class="lbl">
            {{ machineNumberImageLabel }} <span class="text-danger">*</span>
          </label>
          <div class="custom-file-upload-wrapper">
            <input
              type="file"
              ref="machineNumberFileInput"
              @change="handleMachineNumberImageUpload"
              accept="image/*"
              class="d-none"
            />
            <button
              type="button"
              class="upload-button"
              :class="{ 'border-danger': errors.machineNumberImage }"
              @click="$refs.machineNumberFileInput.click()"
            >
              <span v-if="!machineNumberImage">
                <span class="mr-2 text-danger"><CIcon name="cil-camera" size="lg"/></span>
                {{ machineNumberImagePickerText }}
              </span>
              <span v-else>
                <span class="mr-2 text-success"><CIcon name="cil-image-plus" size="lg"/></span>
                {{ machineNumberImage.name }}
              </span>
            </button>
            <CButton
              v-if="machineNumberImage"
              color="danger"
              variant="ghost"
              class="remove-image-button"
              @click="removeMachineNumberImage"
            >
              <i class="cil-x-circle"></i> {{ $t('sendReport.remove') }}
            </CButton>
          </div>
          <small class="text-muted mt-2 d-block">{{ machineNumberImageHint }}</small>
          <div v-if="errors.machineNumberImage" class="text-danger mt-2">
            {{ errors.machineNumberImage }}
          </div>
        </div>

        <div class="my-3">
          <label class="lbl">{{ $t('sendReport.problemLabel') }} <span class="text-danger">*</span></label>
          <CTextarea
            v-model="formData.problemDetails"
            :placeholder="$t('sendReport.problemPlaceholder')"
            rows="3"
            :class="{ 'is-invalid': errors.problemDetails }"
          />
          <div v-if="errors.problemDetails" class="invalid-feedback d-block">
            {{ errors.problemDetails }}
          </div>
        </div>

        <div class="my-4">
          <label class="lbl">{{ $t('sendReport.imageLabel') }} <span class="text-danger">*</span></label>
          <div class="custom-file-upload-wrapper">
            <!-- Normal Upload (Desktop/iOS/Or Image Selected) -->
            <template v-if="!isAndroid || repairImage">
              <input type="file" ref="fileInput" @change="handleFileUpload" accept="image/*" class="d-none" />
              <button type="button" class="upload-button" :class="{ 'border-danger': errors.problemImage }"
                @click="$refs.fileInput.click()">
                <span v-if="!repairImage">
                  <span class="mr-2 text-danger"><CIcon name="cil-camera" size="lg"/></span> {{ $t('sendReport.pickOrCapture') }}
                </span>
                <span v-else>
                  <span class="mr-2 text-success"><CIcon name="cil-image-plus" size="lg"/></span> {{ repairImage.name }}
                </span>
              </button>
              <CButton v-if="repairImage" color="danger" variant="ghost" class="remove-image-button" @click="removeImage">
                <i class="cil-x-circle"></i> {{ $t('sendReport.remove') }}
              </CButton>
            </template>

            <!-- Android Specific: Separate Buttons -->
            <template v-else>
               <input type="file" ref="cameraInput" @change="handleFileUpload" accept="image/*" capture="environment" class="d-none" />
               <input type="file" ref="galleryInput" @change="handleFileUpload" accept="image/*" class="d-none" />
               
               <button type="button" class="upload-button" :class="{ 'border-danger': errors.problemImage }" @click="$refs.cameraInput.click()">
                   <span>
                       <span class="mr-2 text-danger"><CIcon name="cil-camera" size="lg"/></span> {{ $t('sendReport.takePhoto') }}
                   </span>
               </button>
               
               <button type="button" class="upload-button" :class="{ 'border-danger': errors.problemImage }" @click="$refs.galleryInput.click()">
                   <span>
                       <span class="mr-2 text-primary"><CIcon name="cil-image-plus" size="lg"/></span> {{ $t('sendReport.choosePhoto') }}
                   </span>
               </button>
            </template>
          </div>
          <small class="text-muted mt-2 d-block">{{ $t('sendReport.imageHint') }}</small>
          <div v-if="errors.problemImage" class="text-danger mt-2">
            {{ errors.problemImage }}
          </div>
        </div>

        <div class="mb-3">
          <label class="lbl">{{ $t('sendReport.workArea') }} <span class="text-danger">*</span></label>
          <div class="area-grid">
            <label class="area-card" :class="{ active: formData.locationType === 'building' }">
              <input type="radio" value="building" v-model="formData.locationType" class="d-none" />
              <div class="mb-1 text-primary"><CIcon name="cil-building" size="xl"/></div>
              <div>
                <strong>{{ $t('sendReport.insideBuilding') }}</strong><br />
                <small class="text-muted">{{ $t('sendReport.insideBuildingSub') }}</small>
              </div>
              <div class="check">✓</div>
            </label>
            <label class="area-card" :class="{ active: formData.locationType === 'outdoor' }">
              <input type="radio" value="outdoor" v-model="formData.locationType" class="d-none" />
              <div class="mb-1 text-success"><CIcon name="cil-location-pin" size="xl"/></div>
              <div>
                <strong>{{ $t('sendReport.outsideBuilding') }}</strong><br />
                <small class="text-muted">{{ $t('sendReport.outsideBuildingSub') }}</small>
              </div>
              <div class="check">✓</div>
            </label>
          </div>
          <div v-if="errors.locationType" class="text-danger mt-2">
            {{ errors.locationType }}
          </div>
        </div>

        <transition name="fade">
          <div v-if="formData.locationType === 'building'" class="sub-box">
            <div class="d-flex align-items-start">
              <div class="flex-grow-1" style="min-width: 0;">
                <label class="lbl">{{ $t('sendReport.buildingRoom') }} <span class="text-danger">*</span></label>
                <div class="position-relative">
                  <input 
                    type="text"
                    ref="buildingInput"
                    class="form-control" 
                    v-model="buildingSearchDetails" 
                    :placeholder="$t('sendReport.buildingPlaceholder')"
                    @input="onBuildingInputChange"
                    @focus="showBuildingSuggestions = true"
                    @blur="showBuildingSuggestions = false"
                    :class="{ 'is-invalid': errors.buildingId }"
                    autocomplete="off"
                  >
                  <!-- Suggestion Dropdown -->
                  <ul v-if="showBuildingSuggestions && buildingSuggestions.length > 0 && (!buildingSearchDetails.includes('-') || !formData.buildingId)" class="list-group position-absolute w-100 shadow bg-white" style="z-index: 1050; max-height: 250px; overflow-y: auto; background-color: white;">
                    <li 
                      v-for="b in buildingSuggestions" 
                      :key="b._id" 
                      class="list-group-item list-group-item-action cursor-pointer py-2 bg-white" 
                      @mousedown.prevent="selectBuildingSuggestion(b)"
                      style="cursor: pointer;"
                    >
                      <i class="cil-building mr-2 text-primary"></i>
                      <span v-if="b.BUILDINGCODE"><strong>{{ b.BUILDINGCODE }}</strong> - </span>{{ b.BUILDINGNAME || b.name }}
                    </li>
                  </ul>
                  <!-- Dropdown removed as per request -->
                  <!-- Dropdown removed as per request -->
                  <div v-if="buildingSearchDetails && (!buildingSearchDetails.includes('-') || buildingSearchDetails.split('-').filter(Boolean).length < 2)" class="text-danger small mt-1 ml-1">
                    {{ $t('sendReport.buildingFormatHint') }}
                  </div>
                  <!-- Show building name if found for confirmation -->
                  <div v-if="selectedBuilding" class="text-success small mt-1 ml-1">
                     <i class="cil-check-circle"></i> {{ $t('sendReport.buildingFound') }} {{ selectedBuilding.BUILDINGNAME || selectedBuilding.name }}
                  </div>
                </div>
                <div v-if="errors.buildingId" class="invalid-feedback d-block">
                  {{ errors.buildingId }}
                </div>
              </div>
            </div>
          </div>
          <div v-else-if="formData.locationType === 'outdoor'" class="sub-box">
            <label class="lbl">{{ $t('sendReport.pinLocation') }} <span class="text-danger">*</span></label>
            <div class="border rounded overflow-hidden">
              <GoogleMapsPicker v-model="outdoorLocation" @input="onLocationChange" />
            </div>
            <div v-if="errors.location" class="text-danger mt-2">
              {{ errors.location }}
            </div>
          </div>
        </transition>

        <hr class="my-4 border-dashed" />

        <div class="d-flex align-items-center mb-4">
          <div class="icon-box bg-gradient-primary mr-3">
            <span class="text-white"><CIcon name="cil-user" size="xl" /></span>
          </div>
          <div>
            <h5 class="m-0 font-weight-bold">{{ $t('sendReport.reporterSection') }}</h5>
            <small class="text-muted">{{ $t('sendReport.reporterHint') }}</small>
          </div>
        </div>

        <div class="mb-4">
          <CRow>
            <CCol col="12" md="6" class="mb-3">
              <div class="mb-3">
                <label class="lbl">{{ $t('sendReport.reporterName') }}</label>
                <CInput 
                  id="reporterNameInput"
                  name="reporterName"
                  :value="`${currentUser.name} ${currentUser.surname}`" 
                  class="mb-0" 
                  plaintext 
                  readonly 
                />
              </div>
              <div class="mb-3">
                <label class="lbl" for="onBehalfOfInput">{{ $t('sendReport.onBehalfLabel') }}</label>
                <CInput 
                  id="onBehalfOfInput"
                  name="onBehalfOf"
                  v-model="formData.onBehalfOf" 
                  :placeholder="$t('sendReport.onBehalfPlaceholder')" 
                  autocomplete="off"
                />
              </div>
            </CCol>
            <CCol col="12" md="6" class="mb-3">
              <label class="lbl" for="personalPhoneInput">
                {{ $t('sendReport.personalPhone') }} <span class="text-danger">*</span>
              </label>
              <CInput 
                id="personalPhoneInput"
                name="personalPhone"
                v-model="formData.personalPhone" 
                :placeholder="$t('sendReport.phonePlaceholderMobile')" 
                maxlength="10"
                @input="formData.personalPhone = formData.personalPhone.replace(/[^0-9]/g, '')"
                :class="{ 'is-invalid': errors.personalPhone }" 
              />
              <div v-if="errors.personalPhone" class="invalid-feedback d-block">
                {{ errors.personalPhone }}
              </div>
            </CCol>
          </CRow>
          <CRow class="mt-2">
            <CCol col="12" md="6" class="mb-3">
              <label class="lbl" for="telephoneWorkInput">
                {{ $t('sendReport.workPhone') }}
              </label>
              <CInput 
                id="telephoneWorkInput"
                name="telephoneWork"
                v-model="formData.telephoneWork" 
                :placeholder="$t('sendReport.phonePlaceholderWork')" 
                maxlength="10"
                @input="formData.telephoneWork = formData.telephoneWork.replace(/[^0-9]/g, '')"
                :class="{ 'is-invalid': errors.telephoneWork }" 
              />
              <div v-if="errors.telephoneWork" class="invalid-feedback d-block">
                {{ errors.telephoneWork }}
              </div>
            </CCol>
            <CCol col="12" md="6" class="mb-3">
              <label class="lbl">{{ $t('sendReport.availability') }} <span v-if="formData.locationType !== 'outdoor'" class="text-danger">*</span></label>
              <div class="date-time-container">
                 <div class="mb-2">
                    <label class="time-label mb-1" for="datePickerInput">{{ $t('sendReport.dateLabel') }}</label>
                    <input 
                      id="datePickerInput"
                      name="selectedDate"
                      type="date" 
                      v-model="formData.selectedDate"
                      class="form-control"
                      :min="minDate"
                      :class="{ 'is-invalid': errors.availableTime }"
                      @change="updateAvailableTime"
                    />
                 </div>
                 <div class="time-range-container">
                    <div class="time-input-group">
                      <label class="time-label" for="startTimeInput">{{ $t('sendReport.startLabel') }}</label>
                      <input 
                        id="startTimeInput"
                        name="startTime"
                        type="time" 
                        v-model="formData.startTime" 
                        class="form-control time-input"
                        :class="{ 'is-invalid': errors.availableTime }"
                        @change="updateAvailableTime"
                      />
                    </div>
                    <div class="time-separator">-</div>
                    <div class="time-input-group">
                      <label class="time-label" for="endTimeInput">{{ $t('sendReport.endLabel') }}</label>
                      <input 
                        id="endTimeInput"
                        name="endTime"
                        type="time" 
                        v-model="formData.endTime" 
                        class="form-control time-input"
                        :class="{ 'is-invalid': errors.availableTime }"
                        @change="updateAvailableTime"
                      />
                    </div>
                  </div>
              </div>
              <small class="text-muted">{{ $t('sendReport.availabilityHint') }}</small>
              <div v-if="errors.availableTime" class="invalid-feedback d-block">
                {{ errors.availableTime }}
              </div>
            </CCol>
          </CRow>
        </div>

      </CForm>
    </CModalBody>

    <template #footer>
      <div class="d-flex w-100">
        <CButton color="danger" variant="outline" class="flex-grow-1 mr-2 font-weight-bold" @click="closeModal"
          :disabled="isSubmitting">
          {{ $t('sendReport.cancel') }}
        </CButton>
        <CButton color="success" class="flex-grow-1 font-weight-bold text-white" @click="sendReport"
          :disabled="isSubmitting">
          <span v-if="!isSubmitting">
            <i class="cil-send mr-1"></i> {{ $t('sendReport.confirm') }}
          </span>
          <span v-else>
            <span class="spinner-border spinner-border-sm mr-1"></span> {{ $t('sendReport.submitting') }}
          </span>
        </CButton>
      </div>
    </template>
  </CModal>
</template>

<script>
import GoogleMapsPicker from "./UGoogleMapsPicker.vue";
import { mapGetters } from "vuex";
import Swal from "sweetalert2";

export default {
  name: "SendReport",
  components: {
    GoogleMapsPicker
  },
  props: { value: Boolean },
  data: () => ({
    localModal: false,
    isSubmitting: false,
    outdoorLocation: {},
    repairImage: null,
    machineNumberImage: null,
    categories: [],
    buildings: [],
    selectedBuilding: null,
    appUrl: process.env.VUE_APP_APP_URL || "",
    formData: {
      selectedBuildingObject: null, 
      personalPhone: "",
      telephoneWork: "",
      onBehalfOf: "",
      categoryType: "predefined", // NEW: 'predefined' or 'other'
      selectedSubCategory: null, // CHANGED: now a single object
      otherCategoryText: "", // NEW: for other category input
      selectedDate: "", // NEW: Date picker
      problemDetails: "",
      availableTime: "",
      startTime: "",
      endTime: "",
      locationType: "",
      buildingId: "",
      roomName: "",
      latitude: null,
      longitude: null,
      problemImage: "",
    },
    errors: {},
    imageError: "",
    machineNumberImageError: "",
    buildingSearchDetails: "", // NEW: For datalist input model
    showBuildingSuggestions: false,
    isBuildingSelected: false,
    isAndroid: false,
  }),
  computed: {
    ...mapGetters("auth", ["profile"]),

    currentUser() {
      return this.profile || {
        _id: '',
        name: this.$t('sendReport.defaultFirstName'),
        surname: this.$t('sendReport.defaultLastName'),
        email: ''
      };
    },

    requiresMachineNumberImage() {
      const selected = this.formData.selectedSubCategory;
      if (!selected || selected.value === 'other') return false;

      const name = `${selected.value || ''} ${selected.label || ''}`
        .toLowerCase()
        .replace(/\s+/g, '');
      const isComputer = name.includes('คอมพิวเตอร์') || name.includes('computer');
      const hasAssetIdentifier =
        name.includes('เช่า') ||
        name.includes('ครุภัณฑ์') ||
        name.includes('rental') ||
        name.includes('leased') ||
        name.includes('asset');

      return isComputer && hasAssetIdentifier;
    },

    machineNumberImageLabel() {
      return this.$store.state.locale.lang === 'en'
        ? 'Machine number / asset number photo'
        : 'รูปหมายเลขเครื่อง / เลขครุภัณฑ์';
    },

    machineNumberImagePickerText() {
      return this.$store.state.locale.lang === 'en'
        ? 'Attach machine number photo'
        : 'แนบรูปหมายเลขเครื่อง';
    },

    machineNumberImageHint() {
      return this.$store.state.locale.lang === 'en'
        ? 'Take a clear photo showing the machine or asset number'
        : 'กรุณาถ่ายให้เห็นหมายเลขเครื่องหรือเลขครุภัณฑ์อย่างชัดเจน';
    },

    allSubCategoryOptions() {
      void this.$store.state.locale.lang
      const allSubs = [];
      
      this.categories.forEach(cat => {
        if (cat.subCategories && Array.isArray(cat.subCategories)) {
          cat.subCategories.forEach(sub => {
            if (sub.isActive !== false) {
              allSubs.push({
                value: sub.name,
                label: `${sub.name}`,
                categoryId: cat._id,
                categoryName: cat.departmentName,
                slaHours: sub.slaHours,
                icon: sub.icon || null
              });
            }
          });
        }
      });

      // Add Other Option Last
      allSubs.push({
          value: 'other',
          label: this.$t('common.other'),
          categoryId: 'other',
          categoryName: 'Other',
          slaHours: 24
      });
      
      return allSubs;
    },

    minDate() {
      const today = new Date();
      const yyyy = today.getFullYear();
      let mm = today.getMonth() + 1; // Months start at 0
      let dd = today.getDate();

      if (dd < 10) dd = '0' + dd;
      if (mm < 10) mm = '0' + mm;

      return yyyy + '-' + mm + '-' + dd;
    },

    buildingSuggestions() {
      const input = this.buildingSearchDetails;
      if (!input) return [];

      const parts = input.split('-');
      // We generally want to show suggestions for the building part
      const beforeDash = parts[0].toLowerCase().replace(/\s+/g, '');
      if (!beforeDash) return [];

      const maxSuggestions = 10;
      let count = 0;

      return this.buildings.filter(b => {
        if (count >= maxSuggestions) return false;
        
        const code = b.BUILDINGCODE ? b.BUILDINGCODE.toLowerCase().replace(/\s+/g, '') : '';
        const name = b.BUILDINGNAME ? b.BUILDINGNAME.toLowerCase().replace(/\s+/g, '') : (b.name ? b.name.toLowerCase().replace(/\s+/g, '') : '');
        
        if (code.includes(beforeDash) || name.includes(beforeDash)) {
           count++;
           return true;
        }
        return false;
      });
    },
  },
  watch: {
    value(val) {
      this.localModal = val;
      if (val) {
        this.loadData();
      }
    },
    localModal(val) {
      this.$emit("input", val);
      if (!val) {
        this.resetForm();
      }
    },
    'formData.selectedSubCategory'() {
      if (!this.requiresMachineNumberImage) {
        this.removeMachineNumberImage();
        this.$delete(this.errors, 'machineNumberImage');
      }
    },
  },
  created() {
    this.localModal = this.value;
  },
  mounted() {
    this.isAndroid = /Android/i.test(navigator.userAgent);
    this.loadData();
  },
  methods: {
    async loadData() {
      try {
        // Load categories
        const categoryResponse = await fetch(`${process.env.VUE_APP_APP_URL}/category`);
        const categoryResult = await categoryResponse.json();
        if (categoryResult.success) {
          this.categories = categoryResult.data;
        }

        // Load buildings
        const buildingResponse = await fetch(`${process.env.VUE_APP_APP_URL}/building`);
        const buildingResult = await buildingResponse.json();
        this.buildings = Array.isArray(buildingResult) ? buildingResult : (buildingResult.data || []);

        // Auto-fill contact info
        await this.autoFillContactInfo();

      } catch (error) {
        console.error('Failed to load data:', error);
      }
    },

    async autoFillContactInfo() {
      if (!this.currentUser || !this.currentUser._id) return;

      try {
        const userId = this.currentUser._id;
        
        // Fetch both active and history tickets
        const [activeRes, historyRes] = await Promise.all([
            fetch(`${process.env.VUE_APP_APP_URL}/ticket/user/${userId}/active`),
            fetch(`${process.env.VUE_APP_APP_URL}/ticket/user/${userId}/history`)
        ]);

        const activeData = await activeRes.json();
        const historyData = await historyRes.json();

        let allTickets = [];
        if (activeData.success && Array.isArray(activeData.data)) {
            allTickets = allTickets.concat(activeData.data);
        }
        if (historyData.success && Array.isArray(historyData.data)) {
            allTickets = allTickets.concat(historyData.data);
        }

        if (allTickets.length > 0) {
            // Sort by createdAt descending (newest first)
            allTickets.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
            
            const latestTicket = allTickets[0];
            
            if (latestTicket && latestTicket.userPart) {
                // Initialize clean contact info object
                const contactInfo = {};

                // Auto-fill phones
                if (latestTicket.userPart.personalPhone) {
                    contactInfo.personalPhone = latestTicket.userPart.personalPhone;
                }
                    if (latestTicket.userPart.telephoneWork && 
                    latestTicket.userPart.telephoneWork !== '-' && 
                    latestTicket.userPart.telephoneWork !== '0000000000') {
                   contactInfo.telephoneWork = latestTicket.userPart.telephoneWork;
                }

                // Auto-fill available time
                if (latestTicket.userPart.availableTime && latestTicket.userPart.availableTime !== '00:00 - 00:00') {
                    // Extract HH:mm - HH:mm from string like "7 เมษายน 2569 เวลา 08:00 - 15:00"
                    const timeMatch = latestTicket.userPart.availableTime.match(/(\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2})$/);
                    if (timeMatch) {
                        contactInfo.startTime = timeMatch[1];
                        contactInfo.endTime = timeMatch[2];
                        contactInfo.availableTime = latestTicket.userPart.availableTime;
                    }
                }

                // Save to cache
                this.lastContactInfo = contactInfo;

                // Always apply latest info to form
                this.formData.personalPhone = contactInfo.personalPhone || "";
                this.formData.telephoneWork = contactInfo.telephoneWork || "";
                
                if (contactInfo.startTime && contactInfo.endTime) {
                    this.formData.startTime = contactInfo.startTime;
                    this.formData.endTime = contactInfo.endTime;
                    this.updateAvailableTime();
                } else {
                    this.formData.startTime = "";
                    this.formData.endTime = "";
                    this.formData.availableTime = "";
                }
            }
        }
      } catch (error) {
        console.error("Error auto-filling contact info:", error);
      }
    },

    onBuildingChange(selectedOption) {
      if (selectedOption) {
        this.formData.buildingId = selectedOption.value;
        this.selectedBuilding = this.buildings.find(
          b => String(b._id) === String(this.formData.buildingId)
        );
      } else {
        this.formData.buildingId = "";
        this.selectedBuilding = null;
      }

      this.formData.roomName = "";
      this.errors.buildingId = "";
      this.errors.roomName = "";
    },

    onBuildingInputChange() {
      // Reset logic
      this.formData.buildingId = "";
      this.selectedBuilding = null;
      this.errors.buildingId = ""; 
      this.showBuildingSuggestions = true;
      
      const input = this.buildingSearchDetails || "";
      this.formData.roomName = input; // Store full input in roomName initially
      
      if (!input || !input.includes('-')) return;

      const parts = input.split('-');
      // Need at least 2 parts initially to form building-room structure
      if (parts.length < 2) return;

      let bestMatch = null;
      let bestRoom = "";

      // Try forming building code from increasing number of parts (greedy match for building code)
      // e.g. Input: "A1-2-202"
      // Iteration 1: Code="A1", Room="2-202". If A1 exists, match=A1.
      // Iteration 2: Code="A1-2", Room="202". If A1-2 exists, match=A1-2. Overwrites previous.
      for (let i = 1; i < parts.length; i++) {
         const codeCandidate = parts.slice(0, i).join('-').trim();
         const roomCandidate = parts.slice(i).join('-').trim();
         
         if (!codeCandidate) continue;

         const q = codeCandidate.toLowerCase().replace(/\s+/g, '');
         // Find relaxed match for this candidate
         const match = this.buildings.find(b => {
            const code = b.BUILDINGCODE ? b.BUILDINGCODE.toLowerCase().replace(/\s+/g, '') : '';
            // Only check code strict start or exact match? Or keeping relaxed includes?
            // User request: "A1-2" matches "A1-2".
            // If we use includes, "A1" (candidate) matches "A1-2" (building).
            // But we want to distinguish.
            // Let's stick to relaxed include for now as per previous instruction, 
            // but the loop logic prioritizes the longer candidate string which is "more specific".
            const name = b.BUILDINGNAME ? b.BUILDINGNAME.toLowerCase().replace(/\s+/g, '') : (b.name ? b.name.toLowerCase().replace(/\s+/g, '') : '');
            return code.includes(q) || name.includes(q);
         });

         if (match) {
             bestMatch = match;
             bestRoom = roomCandidate;
         }
      }

      if (bestMatch) {
        this.formData.buildingId = bestMatch._id;
        this.formData.roomName = bestRoom;
        this.selectedBuilding = bestMatch;
        this.errors.buildingId = "";
      }
    },

    // Remove selectBuildingFromSuggestion as it's no longer used
    selectBuildingSuggestion(building) {
      if (building.BUILDINGCODE) {
        this.buildingSearchDetails = `${building.BUILDINGCODE}-`;
      } else {
        this.buildingSearchDetails = `${building.BUILDINGNAME || building.name}-`;
      }
      this.showBuildingSuggestions = false;
      this.onBuildingInputChange();
      
      // Keep focus in input to type room number
      this.$nextTick(() => {
        if (this.$refs.buildingInput) {
          this.$refs.buildingInput.focus();
        }
      });
    },

    onRoomChange(event) {
      // Remove room validation since it's now a text input
      const value = event && event.target ? event.target.value : event;
      this.errors.roomName = "";
    },

    updateAvailableTime() {
      // Clear previous errors
      this.errors.availableTime = "";

      if (this.formData.selectedDate && this.formData.selectedDate < this.minDate) {
        this.errors.availableTime = this.$store.state.locale.lang === 'en'
          ? 'The preferred date cannot be in the past'
          : 'ไม่สามารถเลือกวันที่ย้อนหลังได้';
        this.formData.selectedDate = "";
        this.formData.availableTime = "";
        return;
      }

      if (!this.formData.startTime || !this.formData.endTime) {
        this.formData.availableTime = "";
        return;
      }

      // Validate time range - must be within 08:00-15:00
      const startTime = this.formData.startTime;
      const endTime = this.formData.endTime;
      
      // Convert time strings to minutes for easier comparison
      const timeToMinutes = (timeStr) => {
        const [hours, minutes] = timeStr.split(':').map(Number);
        return hours * 60 + minutes;
      };
      
      const startMinutes = timeToMinutes(startTime);
      const endMinutes = timeToMinutes(endTime);
      
      // Removed range validation

      
      // Check if end time is after start time
      if (startMinutes === 0 && endMinutes === 0) {
        // Allow 00:00 - 00:00 (Special case)
      } else if (endMinutes <= startMinutes) {
        this.errors.availableTime = this.$t('sendReport.errEndAfterStart');
        this.formData.availableTime = "";
        // Show alert for day-spanning time range
        // Alert removed as per user request
        // Reset time inputs
        this.formData.startTime = "";
        this.formData.endTime = "";
        return;
      }
      
      // Format the time range string
      let datePart = "";
      if (this.formData.selectedDate) {
         const d = new Date(this.formData.selectedDate);
         if (this.$store.state.locale.lang === 'en') {
           datePart =
             d.toLocaleDateString('en-GB', {
               year: 'numeric',
               month: 'short',
               day: 'numeric',
             }) + this.$t('sendReport.timeSuffix');
         } else {
           // month:'short' with th-TH often still shows English (Apr); use long month names
           datePart =
             d.toLocaleDateString('th-TH', {
               year: 'numeric',
               month: 'long',
               day: 'numeric',
             }) + this.$t('sendReport.timeSuffix');
         }
      }
      
      this.formData.availableTime = `${datePart}${startTime} - ${endTime}`;
    },


    onLocationChange(location) {
      this.formData.latitude = location.lat;
      this.formData.longitude = location.lng;
      this.errors.location = "";
    },

    compressImage(file, quality = 0.8, maxWidth = 1200) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
          const img = new Image();
          img.src = event.target.result;
          img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            let width = img.width;
            let height = img.height;

            if (width > height) {
              if (width > maxWidth) {
                height *= maxWidth / width;
                width = maxWidth;
              }
            } else {
              if (height > maxWidth) {
                width *= maxWidth / height;
                height = maxWidth;
              }
            }

            canvas.width = width;
            canvas.height = height;
            ctx.drawImage(img, 0, 0, width, height);

            canvas.toBlob((blob) => {
              if (!blob) {
                reject(new Error('Compression failed'));
                return;
              }
              const compressedFile = new File([blob], file.name.replace(/\.[^/.]+$/, ".jpg"), {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              resolve(compressedFile);
            }, 'image/jpeg', quality);
          };
          img.onerror = (err) => reject(err);
        };
        reader.onerror = (err) => reject(err);
      });
    },

    async handleFileUpload(event) {
      const file = event.target.files[0];
      this.imageError = ""; // Reset error
      
      if (!file) return;

      if (!file.type.startsWith('image/')) {
        this.imageError = this.$t('sendReport.errImageOnly');
        this.repairImage = null;
        this.$set(this.errors, 'problemImage', this.imageError);
        return;
      }
      
      try {
        // Compress image to ensure reliable upload
        // This will resize to max 1200px width/height and reduce quality slightly
        // converting 9MB+ photos to typically < 500KB
        const compressedFile = await this.compressImage(file);
        this.repairImage = compressedFile;
        
        // Clear errors
        this.imageError = "";
        this.$set(this.errors, 'problemImage', "");
        
      } catch (error) {
        console.error("Image processing error:", error);
        this.imageError = this.$t('sendReport.errImageProcess');
        this.repairImage = null;
        this.$set(this.errors, 'problemImage', this.imageError);
      }
    },

    removeImage() {
      this.repairImage = null;
      this.imageError = "";
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
    },

    async handleMachineNumberImageUpload(event) {
      const file = event.target.files[0];
      this.machineNumberImageError = "";
      if (!file) return;

      if (!file.type.startsWith('image/')) {
        this.machineNumberImageError = this.$t('sendReport.errImageOnly');
        this.machineNumberImage = null;
        this.$set(this.errors, 'machineNumberImage', this.machineNumberImageError);
        return;
      }

      try {
        this.machineNumberImage = await this.compressImage(file);
        this.$set(this.errors, 'machineNumberImage', "");
      } catch (error) {
        console.error("Machine number image processing error:", error);
        this.machineNumberImageError = this.$t('sendReport.errImageProcess');
        this.machineNumberImage = null;
        this.$set(this.errors, 'machineNumberImage', this.machineNumberImageError);
      }
    },

    removeMachineNumberImage() {
      this.machineNumberImage = null;
      this.machineNumberImageError = "";
      if (this.$refs.machineNumberFileInput) {
        this.$refs.machineNumberFileInput.value = "";
      }
    },

    validateForm() {
      const errors = {};
      let isValid = true;

      // เพิ่มตัวเช็คความถูกต้องของเบอร์โทร
      const phoneRegex = /^\d{9,10}$/;

      if (!this.formData.personalPhone) {
        errors.personalPhone = this.$t('sendReport.errPhoneRequired');
        isValid = false;
      } else if (!phoneRegex.test(this.formData.personalPhone)) {
        errors.personalPhone = this.$t('sendReport.errPhoneDigits');
        isValid = false;
      }

      if (this.formData.telephoneWork && this.formData.telephoneWork.trim() !== '' && !phoneRegex.test(this.formData.telephoneWork)) {
        errors.telephoneWork = this.$t('sendReport.errPhoneDigits');
        isValid = false;
      }

      // Validate time range
      if (this.formData.locationType !== 'outdoor') {
        if (!this.formData.selectedDate) {
            errors.availableTime = this.$t('sendReport.errPickDate');
            isValid = false;
        } else if (this.formData.selectedDate < this.minDate) {
            errors.availableTime = this.$store.state.locale.lang === 'en'
              ? 'The preferred date cannot be in the past'
              : 'ไม่สามารถเลือกวันที่ย้อนหลังได้';
            isValid = false;
        } else if (!this.formData.startTime || !this.formData.endTime) {
            errors.availableTime = this.$t('sendReport.errPickTime');
            isValid = false;
        } else {
            // Recalculate and check one last time
            this.updateAvailableTime();
            if (!this.formData.availableTime) {
                // Check if it's because of time-range (end <= start)
                const startMin = this.formData.startTime.split(':').map(Number).reduce((h, m) => h * 60 + m);
                const endMin = this.formData.endTime.split(':').map(Number).reduce((h, m) => h * 60 + m);
                if (endMin <= startMin) {
                   errors.availableTime = this.$t('sendReport.errEndAfterStart');
                } else {
                   errors.availableTime = this.$t('sendReport.errPickTime');
                }
                isValid = false;
            }
        }
      }

      // Validate categories
      if (!this.formData.selectedSubCategory) {
        errors.subCategories = this.$t('sendReport.errPickJobType');
        isValid = false;
      } else if (this.formData.selectedSubCategory.value === 'other') {
        if (!this.formData.otherCategoryText || this.formData.otherCategoryText.trim() === '') {
          errors.otherCategoryText = this.$t('sendReport.errSpecifyOther');
          isValid = false;
        }
      }

      if (!this.formData.locationType) {
        errors.locationType = this.$t('sendReport.errWorkArea');
        isValid = false;
      }

      if (this.formData.locationType === 'building') {
        const inputStr = this.buildingSearchDetails || "";
        const parts = inputStr.split('-');
        if (!inputStr.includes('-') || parts.filter(p => p.trim()).length < 2) {
          errors.buildingId = this.$t('sendReport.errBuildingFormat');
          isValid = false;
        } else {
          this.formData.roomName = inputStr.trim();
        }
      } else if (this.formData.locationType === 'outdoor') {
        if (!this.formData.latitude || !this.formData.longitude) {
          errors.location = this.$t('sendReport.errMapPin');
          isValid = false;
        }
      }

      if (!this.formData.problemDetails || this.formData.problemDetails.trim() === '') {
        errors.problemDetails = this.$t('sendReport.errProblem');
        isValid = false;
      }

      if (this.requiresMachineNumberImage) {
        if (this.machineNumberImageError) {
          errors.machineNumberImage = this.machineNumberImageError;
          isValid = false;
        } else if (!this.machineNumberImage) {
          errors.machineNumberImage = this.$store.state.locale.lang === 'en'
            ? 'Please attach a machine number or asset number photo'
            : 'กรุณาแนบรูปหมายเลขเครื่องหรือเลขครุภัณฑ์';
          isValid = false;
        }
      }

      // Check Image
      if (this.imageError) {
        errors.problemImage = this.imageError;
        isValid = false;
      } else if (!this.repairImage) {
        errors.problemImage = this.$t('sendReport.errAttachImage');
        isValid = false;
      }

      this.errors = errors;
      return isValid;
    },

    async uploadImage(imageFile) {
      if (!imageFile) return null;

      const formData = new FormData();
      formData.append('image', imageFile);

      try {
        const response = await fetch(`${process.env.VUE_APP_APP_URL}/upload`, {
          method: 'POST',
          body: formData
        });
        const result = await response.json();
        if (result.success) {
          return result.filename;
        }
        throw new Error('Upload failed');
      } catch (error) {
        console.error('Image upload error:', error);
        throw error;
      }
    },

    async sendReport() {
      if (!this.validateForm()) {
        Swal.fire({
          icon: "warning",
          title: this.$t('sendReport.swalIncompleteTitle'),
          text: this.$t('sendReport.swalIncompleteText'),
          confirmButtonColor: "#f9b115",
        });
        return;
      }

      // Prepare confirmation details
      let jobType = '-';
      if (this.formData.selectedSubCategory) {
          if (this.formData.selectedSubCategory.value === 'other') {
              jobType = `${this.$t('sendReport.otherPrefix')} (${this.formData.otherCategoryText})`;
          } else {
              jobType = this.formData.selectedSubCategory.label;
          }
      }
      let locationDetail = '';
      if (this.formData.locationType === 'building') {
        let buildingDisplay = this.buildingSearchDetails || '-';
        
        // Use full text
        buildingDisplay = this.formData.roomName || this.buildingSearchDetails;

        locationDetail = `
          <div class="d-flex justify-content-between mb-1">
            <span><strong>${this.$t('sendReport.confirmLocation')}</strong></span>
            <span class="text-right text-dark">${buildingDisplay}</span>
          </div>`;
      } else {
        locationDetail = `
          <div class="d-flex justify-content-between mb-1">
            <span><strong>${this.$t('sendReport.confirmLocation')}</strong></span>
            <span class="text-right text-dark">${this.$t('sendReport.outdoorLocated')}</span>
          </div>`;
      }

      const htmlContent = `
        <div class="p-2" style="font-size: 0.95rem; text-align: left;">
          <div class="d-flex justify-content-between mb-1">
            <span><strong>${this.$t('sendReport.confirmJobType')}</strong></span>
            <span class="text-right text-primary font-weight-bold">${jobType}</span>
          </div>
          <div class="d-flex justify-content-between mb-1">
            <span style="min-width: 60px;"><strong>${this.$t('sendReport.confirmProblem')}</strong></span>
            <span class="text-right text-dark" style="word-break: break-word; max-width: 250px;">${this.formData.problemDetails}</span>
          </div>
          ${locationDetail}

          <hr class="my-2 border-light">
          
          ${this.formData.onBehalfOf ? `
          <div class="d-flex justify-content-between mb-1">
            <span><strong>${this.$t('sendReport.confirmOnBehalf')}</strong></span>
            <span class="text-right text-danger">${this.formData.onBehalfOf}</span>
          </div>` : ''}

          <div class="d-flex justify-content-between mb-1">
            <span><strong>${this.$t('sendReport.confirmPersonalPhone')}</strong></span>
            <span class="text-right text-dark">${this.formData.personalPhone}</span>
          </div>
          ${this.formData.telephoneWork ? `
          <div class="d-flex justify-content-between mb-1">
            <span><strong>${this.$t('sendReport.confirmWorkPhone')}</strong></span>
            <span class="text-right text-dark">${this.formData.telephoneWork}</span>
          </div>` : ''}
          <div class="d-flex justify-content-between mb-1">
            <span><strong>${this.$t('sendReport.confirmAvailability')}</strong></span>
            <span class="text-right text-dark">${this.formData.availableTime || '-'}</span>
          </div>
        </div>
      `;

      const result = await Swal.fire({
        title: this.$t('sendReport.swalConfirmTitle'),
        html: htmlContent,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#2eb85c',
        cancelButtonColor: '#e5533d',
        confirmButtonText: this.$t('sendReport.confirm'),
        cancelButtonText: this.$t('sendReport.swalEdit'),
        reverseButtons: true,
        width: '450px'
      });

      if (result.isConfirmed) {
        this.submitTicket();
      }
    },

    async submitTicket() {
      this.isSubmitting = true;

      try {
        const [imageName, machineNumberImageName] = await Promise.all([
          this.uploadImage(this.repairImage),
          this.uploadImage(this.machineNumberImage)
        ]);
        const sub = this.formData.selectedSubCategory;
        const isOther = sub.value === 'other';

        let response, result;

        if (isOther) {
           const ticketData = {
              userId: this.currentUser._id,
              onBehalfOf: this.formData.onBehalfOf,
              personalPhone: this.formData.personalPhone,
              telephoneWork: this.formData.telephoneWork || '-',
              otherCategoryText: this.formData.otherCategoryText,
              problemDetails: this.formData.problemDetails,
              availabilityDate: this.formData.selectedDate || null,
              availableTime: (this.formData.locationType === 'outdoor' && !this.formData.availableTime)
                  ? '00:00 - 00:00' 
                  : this.formData.availableTime,
              locationType: this.formData.locationType,
              problemImage: imageName,
              machineNumberImage: machineNumberImageName
           };

           if (this.formData.locationType === 'building') {
              if (this.formData.buildingId) {
                 ticketData.buildingId = this.formData.buildingId;
              }
              ticketData.roomName = this.formData.roomName;
           } else {
              ticketData.latitude = this.formData.latitude;
              ticketData.longitude = this.formData.longitude;
           }

           response = await fetch(`${process.env.VUE_APP_APP_URL}/ticket/other`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(ticketData)
           });

        } else {
           // Normal Batch Ticket
           const ticketSubCategories = [{
             categoryId: sub.categoryId,
             name: sub.value
           }];

           const ticketData = {
              userId: this.currentUser._id,
              onBehalfOf: this.formData.onBehalfOf,
              personalPhone: this.formData.personalPhone,
              telephoneWork: this.formData.telephoneWork || '-',
              categoryType: 'predefined', 
              subCategories: ticketSubCategories,
              problemDetails: this.formData.problemDetails,
              availabilityDate: this.formData.selectedDate || null,
              availableTime: (this.formData.locationType === 'outdoor' && !this.formData.availableTime)
                  ? '00:00 - 00:00' 
                  : this.formData.availableTime,
              locationType: this.formData.locationType,
              problemImage: imageName,
              machineNumberImage: machineNumberImageName
           };

           if (this.formData.locationType === 'building') {
              if (this.formData.buildingId) {
                 ticketData.buildingId = this.formData.buildingId;
              }
              ticketData.roomName = this.formData.roomName;
           } else {
              ticketData.latitude = this.formData.latitude;
              ticketData.longitude = this.formData.longitude;
           }

           response = await fetch(`${process.env.VUE_APP_APP_URL}/ticket/batch`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(ticketData)
           });
        }

        result = await response.json();

        if (result.success) {
          await Swal.fire({
            icon: "success",
            title: this.$t('sendReport.swalSuccessTitle'),
            confirmButtonColor: "#2ba9a5",
            timer: 2000,
            showConfirmButton: false
          });

          this.$emit('ticket-created', result.data);
          this.closeModal();
        } else {
          throw new Error(result.message || 'Failed to create tickets');
        }

      } catch (error) {
        console.error('Submit error:', error);
        Swal.fire({
          icon: "error",
          title: this.$t('sendReport.swalErrorTitle'),
          text: error.message || this.$t('sendReport.swalErrorText'),
          confirmButtonColor: "#e5533d",
        });
      } finally {
        this.isSubmitting = false;
      }
    },

    closeModal() {
      this.localModal = false;
    },

    resetForm() {
      this.formData = {
        selectedBuildingObject: null, // Reset multiselect
        personalPhone: "",
        telephoneWork: "",
        onBehalfOf: "",
        categoryType: "predefined", // NEW
        selectedSubCategory: null, // CHANGED
        otherCategoryText: "", // NEW
        selectedDate: "",
        problemDetails: "",
        availableTime: "",
        startTime: "",
        endTime: "",
        locationType: "",
        buildingId: "",
        roomName: "",
        latitude: null,
        longitude: null,
        problemImage: "",
      };

      // Restore last contact info from cache if available
      if (this.lastContactInfo) {
        if (this.lastContactInfo.personalPhone) {
            this.formData.personalPhone = this.lastContactInfo.personalPhone;
        }
        if (this.lastContactInfo.telephoneWork) {
            this.formData.telephoneWork = this.lastContactInfo.telephoneWork;
        }
        if (this.lastContactInfo.startTime && this.lastContactInfo.endTime) {
            this.formData.startTime = this.lastContactInfo.startTime;
            this.formData.endTime = this.lastContactInfo.endTime;
            this.updateAvailableTime();
        }
      }

      this.repairImage = null;
      this.machineNumberImage = null;
      this.machineNumberImageError = "";
      this.outdoorLocation = {};
      this.errors = {};
      this.buildingSearchDetails = "";
      this.isBuildingSelected = false;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
      if (this.$refs.machineNumberFileInput) {
        this.$refs.machineNumberFileInput.value = "";
      }
    },

    getJobIcon(name) {
      if (!name) return 'cil-settings';
      const n = name.toLowerCase();
      if (n.includes('ไฟ') || n.includes('electric') || n.includes('plug')) return 'cil-lightbulb';
      if (n.includes('น้ำ') || n.includes('water') || n.includes('plumb') || n.includes('pipe') || n.includes('รั่ว')) return 'cil-drop';
      if (n.includes('แอร์') || n.includes('air') || n.includes('cool') || n.includes('เครื่องปรับอากาศ')) return 'cil-snowflake';
      if (n.includes('เน็ต') || n.includes('wifi') || n.includes('internet') || n.includes('it') || n.includes('network')) return 'cil-wifi-signal-1';
      if (n.includes('คอม') || n.includes('computer') || n.includes('print') || n.includes('server')) return 'cil-monitor';
      if (n.includes('โต๊ะ') || n.includes('เก้าอี้') || n.includes('furniture') || n.includes('ครุภัณฑ์')) return 'cil-couch';
      if (n.includes('ประตู') || n.includes('หน้าต่าง') || n.includes('door') || n.includes('window')) return 'cil-door';
      if (n.includes('อาคาร') || n.includes('build') || n.includes('wall') || n.includes('เพดาน') || n.includes('พื้น')) return 'cil-building';
      if (n.includes('ความสะอาด') || n.includes('clean') || n.includes('ขยะ')) return 'cil-trash';
      if (n.includes('สวน') || n.includes('garden') || n.includes('tree')) return 'cil-flower';
      if (n.includes('อื่น') || n.includes('other')) return 'cil-pencil';
      return 'cil-puzzle';
    },

    getJobIconColor(name) {
       if (!name) return 'text-muted';
       const n = name.toLowerCase();
       if (n.includes('ไฟ')) return 'text-warning';
       if (n.includes('น้ำ')) return 'text-info';
       if (n.includes('แอร์')) return 'text-primary';
       if (n.includes('เน็ต') || n.includes('คอม')) return 'text-dark';
       if (n.includes('โต๊ะ') || n.includes('ประตู')) return 'text-success';
       if (n.includes('ความสะอาด')) return 'text-secondary';
       if (n.includes('อื่น') || n.includes('other')) return 'text-warning';
       return 'text-secondary';
    },

    getCustomIconUrl(label) {
      if (!label) return null;
      const text = label.toLowerCase();
      
      // 1. สวิตซ์ไฟเสีย (Switch)
      if (text.includes('สวิต')) {
        return 'https://cdn-icons-png.flaticon.com/512/2399/2399707.png';
      }
      
      // 2. หลอดไฟเสีย (Broken Bulb) - Broaden to 'หลอด'
      if (text.includes('หลอด')) {
        return 'https://cdn-icons-png.flaticon.com/512/12145/12145173.png';
      }

      // 3. ไฟฟ้า (Electrical) - General
      if (text.includes('ไฟ') || text.includes('electric')) {
        return 'https://cdn-icons-png.flaticon.com/512/9302/9302891.png';
      }

      // 4. ประปา (Plumbing)
      if (text.includes('น้ำ') || text.includes('water') || text.includes('plumb') || text.includes('pipe') || text.includes('ประปา') || text.includes('รั่ว')) {
        return 'https://cdn-icons-png.flaticon.com/512/15551/15551239.png';
      }
      
      // 3. อุปกรณ์ต่อพ่วง (Peripherals) - Broaden to 'ต่อพ่วง'
      if (text.includes('ต่อพ่วง') || text.includes('peripheral') || text.includes('device')) {
        return 'https://cdn-icons-png.flaticon.com/512/2572/2572303.png';
      }
      
      // LAN
      if (text.includes('lan') || text.includes('แลน')) {
        return 'https://cdn-icons-png.flaticon.com/512/4669/4669051.png';
      }

      // 4. เครือข่าย (Network / Wi-Fi)
      if (text.includes('เครือข่าย') || text.includes('network') || text.includes('wifi') || text.includes('wi-fi') || text.includes('เน็ต') || text.includes('ไวไฟ')) {
        return 'https://cdn-icons-png.flaticon.com/512/159/159599.png';
      }
      
      // 5. ซอฟต์แวร์ (Software) - Include common misspellings
      if (text.includes('ซอฟ') || text.includes('ซอป') || text.includes('soft') || text.includes('program')) {
        return 'https://cdn-icons-png.flaticon.com/512/2010/2010990.png';
      }
      
      // Printer
      if (text.includes('print') || text.includes('ปริ้น') || text.includes('พิมพ์')) {
        return 'https://cdn-icons-png.flaticon.com/512/446/446991.png';
      }

      // 6. คอมพิวเตอร์ (Computer)
      if (text.includes('คอม') || text.includes('computer')) {
        return 'https://cdn-icons-png.flaticon.com/512/4330/4330711.png';
      }

      // 7. ฮาร์ดแวร์ (Hardware)
      if (text.includes('ฮาร์ด') || text.includes('ฮาท') || text.includes('hard') || text.includes('device')) {
        return 'https://cdn-icons-png.flaticon.com/512/5071/5071907.png';
      }
      // 8. อาคาร (Building)
      if (text.includes('อาคาร') || text.includes('build') || text.includes('wall') || text.includes('เพดาน') || text.includes('พื้น')) {
        return 'https://cdn-icons-png.flaticon.com/512/15564/15564485.png';
      }

      // 9. โทรศัพท์ (Telephone)
      if (text.includes('โทรศัพท์') || text.includes('phone') || text.includes('tel')) {
        return 'https://cdn-icons-png.flaticon.com/512/953/953831.png';
      }

      // 10. งานโสตทัศนูปกรณ์ (Audio-visual)
      if (text.includes('โสต') || text.includes('audio') || text.includes('visual')) {
        return 'https://cdn-icons-png.flaticon.com/512/11827/11827152.png';
      }

      return null;
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Sarabun:wght@400;600;700&display=swap");

.body-font {
  font-family: "Sarabun", sans-serif;
  background: white;
}

.lbl {
  font-weight: 600;
  color: #495057;
  display: block;
  margin-bottom: 4px;
}

.text-danger {
  color: #e55353;
}

.is-invalid-multiselect>>>.multiselect__tags {
  border-color: #e55353 !important;
}

.icon-box {
  min-width: 48px;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
}

.bg-gradient-primary {
  background: linear-gradient(135deg, #321fdb, #1b0e8c);
}

.bg-gradient-warning {
  background: linear-gradient(135deg, #f9b115, #f6960b);
}

.border-dashed {
  border-top: 1px dashed #ccc;
}



.area-grid {
  display: flex;
  gap: 10px;
}

.category-type-selection {
  display: flex;
  gap: 10px;
}

.category-type-card {
  flex: 1;
  background: #fff;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  padding: 15px;
  text-align: center;
  cursor: pointer;
  position: relative;
  transition: all 0.2s;
  height: 90px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-bottom: 0;
}

.category-type-card:hover {
  border-color: #bdc3c7;
  transform: translateY(-2px);
}

.category-option-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.clickable-label {
  cursor: pointer;
  transition: opacity 0.2s;
}

.clickable-label:hover {
  opacity: 0.8;
}

.category-type-card.active {
  border-color: #2eb85c;
  background: #f0fff4;
  color: #2eb85c;
  box-shadow: 0 6px 12px rgba(46, 184, 92, 0.15);
}

.category-type-card .check {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #fff;
  border: 2px solid #e9ecef;
  color: transparent;
  font-size: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.category-type-card.active .check {
  background: #2eb85c;
  border-color: #2eb85c;
  color: #fff;
}

.area-card {
  flex: 1;
  background: #fff;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  padding: 15px;
  text-align: center;
  cursor: pointer;
  position: relative;
  transition: all 0.2s;
  height: 110px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-bottom: 0;
}

.area-card:hover {
  border-color: #bdc3c7;
  transform: translateY(-2px);
}

.area-card.active {
  border-color: #2eb85c;
  background: #f0fff4;
  color: #2eb85c;
  box-shadow: 0 6px 12px rgba(46, 184, 92, 0.15);
}

.check {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #fff;
  border: 2px solid #e9ecef;
  color: transparent;
  font-size: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.area-card.active .check {
  background: #2eb85c;
  border-color: #2eb85c;
  color: #fff;
}

.sub-box {
  background: #fff;
  padding: 15px;
  border-radius: 8px;
  border: 1px solid #e2e6ea;
  margin-top: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.fade-enter-active {
  transition: 0.3s;
}

.fade-enter,
.fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

.custom-file-upload-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
}

.upload-button {
  flex-grow: 1;
  background: #fff;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  padding: 15px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  height: 110px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: #495057;
  font-weight: 600;
  font-size: 1rem;
}

.upload-button:hover {
  border-color: #bdc3c7;
  transform: translateY(-2px);
}

.upload-button.border-danger {
  border-color: #e55353;
}

.remove-image-button {
  height: 110px;
  padding: 0 15px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.9rem;
}

.remove-image-button i {
  font-size: 1.5rem;
  margin-bottom: 5px;
}

.invalid-feedback {
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

/* Time Range Picker Styles */
.time-range-container {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.time-input-group {
  display: flex;
  align-items: center;
  gap: 5px;
}

.time-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #495057;
  margin: 0;
  min-width: 35px;
}

.time-input {
  width: 120px;
  font-size: 0.875rem;
  padding: 0.375rem 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  background-color: #fff;
}

.time-input:focus {
  border-color: #80bdff;
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.time-input.is-invalid {
  border-color: #dc3545;
}

.time-input::-webkit-calendar-picker-indicator {
  cursor: pointer;
  filter: invert(0.5);
}

.time-input::-webkit-calendar-picker-indicator:hover {
  filter: invert(0.3);
}

.time-separator {
  font-weight: bold;
  color: #6c757d;
  margin: 0 5px;
}

@media (max-width: 576px) {
  .time-range-container {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }
  
  .time-input-group {
    justify-content: space-between;
  }
  
  .time-input {
    width: auto;
    flex: 1;
  }
  
  .time-separator {
    text-align: center;
    margin: 0;
  }
}

/* Custom Multiselect Aesthetic */
::v-deep .multiselect__tags {
  border: 1px solid #ced4da;
  border-radius: 6px;
  min-height: 45px; /* Matches other inputs */
  padding-top: 8px;
}
::v-deep .multiselect__tag {
  background: #2ba9a5;
  color: #fff;
  border-radius: 4px;
  margin-bottom: 0; 
  margin-top: 2px;
}
::v-deep .multiselect__tag-icon:after {
  color: #d1fae5;
}
::v-deep .multiselect__tag-icon:hover {
  background: #208784;
}
::v-deep .multiselect__option--highlight {
  background: #2ba9a5;
  background: linear-gradient(135deg, #2ba9a5 0%, #208784 100%);
}
::v-deep .multiselect__option--highlight:after {
  background: transparent;
}

/* Slow animation for SweetAlert2 */
::v-deep .swal2-show-slow {
  animation: swal2-show-slow 0.6s ease-out !important;
}

::v-deep .swal2-hide-slow {
  animation: swal2-hide-slow 0.5s ease-out forwards !important;
}

@keyframes swal2-show-slow {
  0% {
    transform: scale(0.7);
    opacity: 0;
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes swal2-hide-slow {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(0.5);
    opacity: 0;
  }
}
</style>

<style scoped>
/* Job Type Grid */
.job-type-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(75px, 1fr));
  gap: 8px;
}

.job-item-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
}

.job-item-wrapper:hover .job-card {
  border-color: #bdc3c7;
  transform: translateY(-3px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
}

.job-card {
  background: #fff;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  padding: 6px;
  text-align: center;
  /* cursor: pointer; Valid on wrapper now */
  position: relative;
  transition: all 0.2s cubic-bezier(0.25, 0.8, 0.25, 1);
  height: 60px; /* Revert to smaller size since text is gone */
  width: 60px; /* Make it square */
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  box-shadow: 0 2px 4px rgba(0,0,0,0.02);
}

/* Hover effect is now on .job-item-wrapper */

.job-card.active {
  border-color: #2eb85c;
  background: #f0fff4;
  color: #2eb85c;
  box-shadow: 0 6px 12px rgba(46, 184, 92, 0.15);
}

.job-card .check {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #2eb85c;
  color: white;
  font-size: 10px;
  font-weight: bold;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 2px 4px rgba(46, 184, 92, 0.2);
}

.date-time-container {
  display: flex;
  flex-direction: column;
}

.job-label {
  font-size: 0.7rem;
  font-weight: 600;
  line-height: 1.2;
  word-break: break-word;
  white-space: normal;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2; /* Show max 2 lines */
  line-clamp: 2;
  -webkit-box-orient: vertical;
  color: #555;
}

.small-text {
  font-size: 10px;
}

</style>

